/* =============================================================
   Hue Header — interaccion del encabezado Huechicura
   - Sticky con clase .is-scrolled al pasar el umbral
   - Drawer mobile con foco, ESC y backdrop
   - Sincroniza el cart bubble entre topbar y mobile
   ============================================================= */
(function () {
  'use strict';

  if (window.HueHeaderLoaded) return;
  window.HueHeaderLoaded = true;

  class HueHeader extends HTMLElement {
    constructor() {
      super();
      this.scrollThreshold = 80;
      this.lastScrolled = false;
      this.focusableSelector =
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    }

    connectedCallback() {
      this.isSticky = this.dataset.sticky !== 'false';
      this.drawer = this.querySelector('[data-hue-drawer]');
      this.toggle = this.querySelector('[data-hue-drawer-toggle]');
      this.closers = this.querySelectorAll('[data-hue-drawer-close]');
      this.cartBubble = this.querySelector('#cart-icon-bubble');
      this.cartMirror = this.querySelector('[data-hue-cart-mirror]');

      this.setHeaderHeightVar();
      this.onResize = this.debounce(this.setHeaderHeightVar.bind(this), 150);
      window.addEventListener('resize', this.onResize);

      if (this.isSticky) {
        this.bindSticky();
      }

      if (this.toggle && this.drawer) {
        this.bindDrawer();
      }

      if (this.cartBubble && this.cartMirror) {
        this.bindCartMirror();
      }

      this.bindNavDetailsCloseOnLeave();
      this.bindMegaMenu();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('scroll', this.onScroll, { passive: true });
      document.removeEventListener('keydown', this.onKeydown);
    }

    setHeaderHeightVar() {
      const height = this.offsetHeight;
      document.documentElement.style.setProperty('--hue-header-height', `${height}px`);
    }

    bindSticky() {
      let ticking = false;
      this.onScroll = () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(() => {
          const scrolled = window.scrollY > this.scrollThreshold;
          if (scrolled !== this.lastScrolled) {
            this.classList.toggle('is-scrolled', scrolled);
            document.documentElement.classList.toggle('hue-header-scrolled', scrolled);
            this.lastScrolled = scrolled;
            this.setHeaderHeightVar();
          }
          ticking = false;
        });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
    }

    bindDrawer() {
      this.toggle.addEventListener('click', (e) => {
        e.preventDefault();
        if (this.drawer.classList.contains('is-open')) {
          this.closeDrawer();
        } else {
          this.openDrawer();
        }
      });

      this.closers.forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.closeDrawer();
        });
      });

      this.drawer.querySelectorAll('a').forEach((link) => {
        link.addEventListener('click', () => {
          if (link.getAttribute('href') && link.getAttribute('href').startsWith('#')) return;
          this.closeDrawer();
        });
      });

      this.onKeydown = (e) => {
        if (!this.drawer.classList.contains('is-open')) return;
        if (e.key === 'Escape') {
          e.preventDefault();
          this.closeDrawer();
        }
        if (e.key === 'Tab') {
          this.trapFocus(e);
        }
      };
      document.addEventListener('keydown', this.onKeydown);
    }

    openDrawer() {
      this.drawer.hidden = false;
      requestAnimationFrame(() => {
        this.drawer.classList.add('is-open');
      });
      this.drawer.setAttribute('aria-hidden', 'false');
      this.toggle.setAttribute('aria-expanded', 'true');
      this.toggle.classList.add('is-open');
      document.body.classList.add('hue-drawer-open');
      this.previouslyFocused = document.activeElement;
      const firstClose = this.drawer.querySelector('.hue-header__drawer-close');
      if (firstClose) firstClose.focus({ preventScroll: true });
    }

    closeDrawer() {
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      this.toggle.setAttribute('aria-expanded', 'false');
      this.toggle.classList.remove('is-open');
      document.body.classList.remove('hue-drawer-open');
      const onEnd = () => {
        if (!this.drawer.classList.contains('is-open')) {
          this.drawer.hidden = true;
        }
        this.drawer.removeEventListener('transitionend', onEnd);
      };
      this.drawer.addEventListener('transitionend', onEnd);
      setTimeout(() => {
        if (!this.drawer.classList.contains('is-open')) this.drawer.hidden = true;
      }, 450);
      if (this.previouslyFocused && typeof this.previouslyFocused.focus === 'function') {
        this.previouslyFocused.focus({ preventScroll: true });
      }
    }

    trapFocus(e) {
      const focusables = Array.from(
        this.drawer.querySelectorAll(this.focusableSelector)
      ).filter((el) => el.offsetParent !== null);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    bindCartMirror() {
      const sync = () => {
        if (!this.cartMirror || !this.cartBubble) return;
        this.cartMirror.innerHTML = this.cartBubble.innerHTML;
      };

      const observer = new MutationObserver(sync);
      observer.observe(this.cartBubble, { childList: true, subtree: true, characterData: true });
      this.cartObserver = observer;
    }

    bindNavDetailsCloseOnLeave() {
      const detailsList = this.querySelectorAll('.hue-header__nav-details');
      detailsList.forEach((d) => {
        const li = d.closest('.hue-header__nav-item');
        if (!li) return;
        li.addEventListener('mouseleave', () => {
          if (window.matchMedia('(min-width: 990px)').matches) {
            d.removeAttribute('open');
          }
        });
        d.querySelector('summary').addEventListener('mouseenter', () => {
          if (window.matchMedia('(min-width: 990px)').matches) {
            detailsList.forEach((x) => x !== d && x.removeAttribute('open'));
            d.setAttribute('open', '');
          }
        });
      });

      document.addEventListener('click', (e) => {
        if (!this.contains(e.target) || !window.matchMedia('(min-width: 990px)').matches) return;
        detailsList.forEach((d) => {
          if (!d.contains(e.target)) d.removeAttribute('open');
        });
      });
    }

    bindMegaMenu() {
      this.querySelectorAll('[data-product-types]').forEach((grid) => {
        if (grid.dataset.grouped === 'true') return;
        const normalize = (value) => value.normalize('NFD').replace(/[\u0300\u0301\u0302\u0308]/g, '')
          .normalize('NFC').toLocaleLowerCase('es').trim().replace(/\s+/g, ' ');
        const groups = new Map();
        grid.querySelectorAll('[data-product-type]').forEach((card) => {
          const type = card.dataset.productType;
          const key = normalize(type);
          const group = groups.get(key);
          if (group) {
            group.types.add(type);
            card.remove();
          } else {
            groups.set(key, { card, types: new Set([type]) });
          }
        });
        groups.forEach(({ card, types }) => {
          if (types.size < 2) return;
          const query = Array.from(types, (type) =>
            `product_type:"${type.replace(/[\\"]/g, '\\$&')}"`
          ).join(' OR ');
          const params = new URLSearchParams({ type: 'product', q: `(${query})` });
          card.href = `${grid.dataset.searchUrl}?${params}`;
        });
        grid.dataset.grouped = 'true';

        const menu = grid.closest('[data-product-type-menu]');
        const input = menu?.querySelector('[data-product-type-search]');
        if (!input) return;
        const empty = menu.querySelector('[data-product-type-empty]');
        const entries = Array.from(groups.values(), ({ card, types }) => ({
          card, names: Array.from(types, normalize),
        }));
        const filter = () => {
          const terms = normalize(input.value).split(' ').filter(Boolean);
          let matches = 0;
          entries.forEach(({ card, names }) => {
            const visible = names.some((name) => terms.every((term) => name.includes(term)));
            card.hidden = !visible;
            if (visible) matches++;
          });
          grid.scrollTop = 0;
          if (empty) empty.hidden = matches > 0;
        };
        input.addEventListener('input', filter);
        const controls = menu.querySelector('[data-product-type-controls]');
        if (controls) controls.hidden = false;
        filter();
      });
    }

    debounce(fn, wait) {
      let t;
      return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn.apply(this, args), wait);
      };
    }
  }

  if (!customElements.get('hue-header')) {
    customElements.define('hue-header', HueHeader);
  }
})();
